import { useState, useCallback, useRef, useEffect } from 'react';
import { showConfirm } from '@/utils/common/alert';
import { UploadFile } from '@/components/common/admin/mobile/FileUploadSection';
import { 
  EditMessageFormData,
  EditMessageFieldChangeHandler,
  MessageError
} from '@/types/admin/messageFormTypes';
import { IConversation, IMessageResident, IAccount } from '@/types/admin/messages';

export interface EditMessageFormErrors {
  subject?: string;
  recipients?: string;
}

export const useEditMessage = (conversation: IConversation | null | undefined) => {
  // Initialize form data from conversation
  const getInitialFormData = useCallback((): EditMessageFormData => {
    if (!conversation) {
      return {
        subject: '',
        recipients: [],
        sendEmail: false,
        originalRecipients: [],
      };
    }

    // Extract original recipients from conversation accounts (excluding admins)
    const originalRecipients = conversation.conversationAccounts
      ?.filter((recipient) => recipient.type !== 'admin')
      .map((conversationAccount) => {
        return {
          ...conversationAccount.assigneeAccount,
          leaseCustomers: conversationAccount.assigneeAccount.leaseCustomers || [],
          statusType: 'current', // Default status for existing conversation participants
        };
      }) || [];

    return {
      subject: conversation.subject || '',
      recipients: originalRecipients,
      sendEmail: conversation.sendEmail || false,
      originalRecipients,
    };
  }, [conversation]);

  const [formData, setFormData] = useState<EditMessageFormData>(getInitialFormData());
  const [errors, setErrors] = useState<EditMessageFormErrors>({});
  const initialFormDataRef = useRef<EditMessageFormData>(getInitialFormData());

  // Update form data when conversation changes
  useEffect(() => {
    const newFormData = getInitialFormData();
    setFormData(newFormData);
    initialFormDataRef.current = newFormData;
    setErrors({});
  }, [conversation, getInitialFormData]);

  // Handle field changes
  const handleFieldChange: EditMessageFieldChangeHandler = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when field is updated
    if (errors[field as keyof EditMessageFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  }, [errors]);

  // Validate form
  const validateForm = useCallback((): EditMessageFormErrors => {
    const newErrors: EditMessageFormErrors = {};

    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.length > 100) {
      newErrors.subject = 'Subject must be 100 characters or less';
    }

    // Recipients validation
    if (formData.recipients.length === 0) {
      newErrors.recipients = 'At least one recipient is required';
    }

    setErrors(newErrors);
    return newErrors;
  }, [formData]);

  // Check if form has unsaved changes
  const hasUnsavedChanges = useCallback((): boolean => {
    const current = formData;
    const initial = initialFormDataRef.current;

    return (
      current.subject !== initial.subject ||
      current.recipients.length !== initial.recipients.length ||
      current.sendEmail !== initial.sendEmail ||
      // Check if recipients have actually changed (by ID)
      !current.recipients.every(r => initial.recipients.some(ir => ir.id === r.id)) ||
      !initial.recipients.every(r => current.recipients.some(cr => cr.id === r.id))
    );
  }, [formData]);

  // Show discard alert
  const showDiscardAlert = useCallback(async (onConfirm: () => void) => {
    const confirmed = await showConfirm(
      'Discard Changes',
      'You have unsaved changes. Are you sure you want to discard them?',
      'Discard',
      'Cancel'
    );
    
    if (confirmed) {
      onConfirm();
    }
  }, []);

  // Reset form to initial state
  const resetForm = useCallback(() => {
    const initialData = getInitialFormData();
    setFormData(initialData);
    setErrors({});
    initialFormDataRef.current = initialData;
  }, [getInitialFormData]);

  // Get form validation status
  const isFormValid = useCallback((): boolean => {
    const validationErrors = validateForm();
    return Object.keys(validationErrors).length === 0;
  }, [validateForm]);

  // Get new recipients (not in original list)
  const getNewRecipients = useCallback(() => {
    return formData.recipients.filter(
      (recipient) => !formData.originalRecipients.some(
        (originalRecipient) => originalRecipient.id === recipient.id
      )
    );
  }, [formData.recipients, formData.originalRecipients]);

  // Get recipient stats for display
  const getRecipientStats = useCallback(() => {
    const newRecipients = getNewRecipients();
    const assignedCount = newRecipients.filter(
      (r: any) => r.account && Object.keys(r.account || {}).length > 0
    ).length;

    const unassignedCount = newRecipients.filter(
      (r: any) => !r.account || Object.keys(r.account || {}).length <= 0
    ).length;

    return {
      total: formData.recipients.length,
      originalCount: formData.originalRecipients.length,
      newCount: newRecipients.length,
      assigned: assignedCount,
      unassigned: unassignedCount,
    };
  }, [formData.recipients, formData.originalRecipients, getNewRecipients]);

  return {
    formData,
    errors,
    handleFieldChange,
    validateForm,
    hasUnsavedChanges,
    showDiscardAlert,
    resetForm,
    isFormValid,
    getRecipientStats,
    getNewRecipients,
  };
};